import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'Context/QueryClientContext';
import ObrasApi from 'Data/Obras.api';
import { ICreateObra } from 'Data/interfaces/Obra/ICreateOrUpdate';
import { Mixpanel } from 'Utils/MixPanel';
import { store } from 'Store';
import { NavigateFunction } from 'react-router';
import { Dispatch } from 'redux';
import toastHandler from 'Utils/toastHandler';
import { AuthActions } from 'Store/Auth/Auth.actions';
import { IUserLimitsActive } from 'Store/Auth/interfaces/IAuthState';
import { OBRA_QUERY_KEY } from 'Data/keys/QueryKeys';
import { createError } from 'Utils/Messages';

interface IUseQueryCreateObraProps {
  navigate: NavigateFunction;
  dispatch: Dispatch;
}

export const useQueryCreateObra = ({
  navigate,
  dispatch,
}: IUseQueryCreateObraProps) => {
  const { mutate, isPending } = useMutation({
    mutationFn: (variables: ICreateObra) => {
      return ObrasApi.create(variables.values);
    },
    onSuccess: async ({ data }, variables) => {
      const obra = data?.data;
      if (variables.values.ExistingConstructionSiteTokenId) {
        obra.isConnectedApi = true;
      }

      if (variables.firstObraTracking) {
        Mixpanel.track({
          name: 'CREATE_FIRST_OBRA',
          props: { constructionSiteId: obra.ConstructionSiteId },
          userInfo: variables.userInfoTracking,
          currentListTenant: variables.currentTenantTracking,
        });
      }

      const UsersWithConstruction = store.getState().tenant?.UsersWithConstruction;
      const isFirstUser =
        (UsersWithConstruction?.tenantInvitedUsers?.length || 0) <= 0 &&
        (UsersWithConstruction?.tenantUsers?.length || 0) <= 0 &&
        obra?.InvitedUsers?.length > 0;
      if (isFirstUser) {
        Mixpanel.track({
          name: 'INVITED_FIRST_USER',
          props: { constructionSiteId: obra.ConstructionSiteId },
          userInfo: variables.userInfoTracking,
          currentListTenant: variables.currentTenantTracking,
        });
      }

      const userLimitsActive: IUserLimitsActive = {
        QtyActiveConstructionSites: 1,
        QtyActiveUsers:
          obra?.InvitedUsers?.filter((newUsers: any) => {
            return !UsersWithConstruction?.tenantInvitedUsers
              ?.map((user: any) => {
                return user.InvitedUserEmail;
              })
              .includes(newUsers.Email);
          }).length || undefined,
      };
      dispatch(AuthActions.toogleUserLimitsActive(userLimitsActive));

      queryClient.invalidateQueries({ queryKey: [OBRA_QUERY_KEY], exact: true });
      queryClient.invalidateQueries({
        queryKey: [OBRA_QUERY_KEY, Number(obra.ConstructionSiteId)],
        exact: true,
      });
      if (variables.values.ExistingConstructionSiteTokenId) {
        navigate(`/obras/detail/${obra.ConstructionSiteId}/true`);
      } else {
        window.location.href = data.info.redirectUrl;
      }
    },
    onError: async (error: any) => {
      const errorMsg = error.errors ? error.errors[0].Message : createError('obra');
      toastHandler.showError(errorMsg);
    },
  });

  return {
    createObra: mutate,
    isLoading: isPending,
  };
};
